import { useEffect, useRef, useState } from "react";
import { Alert, Button, Flex, Input, Typography } from "antd";
import { ArrowLeftOutlined, MobileOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import type { RealSmsLoginIssued } from "@wenjing/contract/auth-sms-test";
import { api, setToken, type User } from "../api";
import { authClientContext } from "../auth-client";

export default function RealSmsTestLogin({ onBack, onLogin }: { onBack: () => void; onLogin: (user: User) => void }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const working = useRef(false);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown(value => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const send = async () => {
    if (working.current || countdown > 0) return;
    if (!/^1[3-9]\d{9}$/.test(phone)) { setError("请输入正确的11位手机号"); return; }
    working.current = true; setBusy(true); setError("");
    try {
      // SendSms has no idempotency. Do not retry a POST against a second API host.
      const result = await api<RealSmsLoginIssued>("/api/auth/sms-test/request-code", { method: "POST", body: JSON.stringify({ phone }) }, { allowFallback: false });
      if (result.delivery !== "real_sms" || "testCode" in result) throw new Error("服务器未进入真实短信验证，请更新服务后重试");
      if (alive.current) { setCode(""); setSent(true); setCountdown(60); }
    } catch (reason) {
      if (alive.current) setError(reason instanceof Error ? reason.message : "短信发送未完成，请核对后重试");
    } finally { working.current = false; if (alive.current) setBusy(false); }
  };

  const verify = async () => {
    if (working.current) return;
    if (!/^\d{6}$/.test(code)) { setError("请输入短信中的6位验证码"); return; }
    working.current = true; setBusy(true); setError("");
    try {
      const result = await api<{ token: string; user: User }>("/api/auth/sms-test/verify-code", {
        method: "POST", body: JSON.stringify({ phone, code, ...authClientContext() })
      }, { allowFallback: false });
      if (!result.token || !result.user) throw new Error("登录响应异常，请重新验证");
      if (alive.current) { setToken(result.token); onLogin(result.user); }
    } catch (reason) {
      if (alive.current) setError(reason instanceof Error ? reason.message : "验证失败，请重试");
    } finally { working.current = false; if (alive.current) setBusy(false); }
  };

  useEffect(() => { if (sent) codeRef.current?.focus(); }, [sent]);

  return <Flex vertical gap="middle" aria-label="真实短信测试登录">
    <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} disabled={busy}>返回普通登录</Button>
    <div className="studio-login-panel-head">
      <h2>真实短信测试登录</h2>
      <p>用手机收到的验证码完成登录</p>
    </div>
    <Alert type="info" showIcon message="此入口发送真实短信" description="请使用系统已开通账号的手机号。发送次数会记入账号所属租户，普通试运行登录仍可使用。" />
    <form onSubmit={event => { event.preventDefault(); void (sent ? verify() : send()); }}>
      <Flex vertical gap="middle">
        <div className="studio-login-field">
          <label htmlFor="real-sms-phone">账号手机号</label>
          <Input id="real-sms-phone" size="large" prefix={<MobileOutlined />} value={phone} maxLength={11} inputMode="tel" autoComplete="tel" placeholder="输入已开通账号的手机号" disabled={busy || sent} onChange={event => { setPhone(event.target.value.replace(/\D/g, "")); setError(""); }} />
        </div>
        {sent && <>
          <Typography.Text role="status">若该手机号已开通账号，验证码将发送至 {phone.slice(0, 3)}****{phone.slice(-4)}，5分钟内有效。</Typography.Text>
          <div className="studio-login-field">
            <label htmlFor="real-sms-code">短信验证码</label>
            <Input id="real-sms-code" size="large" value={code} maxLength={6} inputMode="numeric" autoComplete="one-time-code" placeholder="输入手机收到的6位验证码" disabled={busy} ref={input => { codeRef.current = input?.input || null; }} onChange={event => setCode(event.target.value.replace(/\D/g, ""))} />
          </div>
        </>}
        {error && <Alert type="error" showIcon message={error} />}
        <Button type="primary" size="large" block htmlType="submit" loading={busy} icon={<SafetyCertificateOutlined />}>{sent ? "验证短信并登录" : "发送真实短信验证码"}</Button>
        {sent && <Flex wrap gap="small" justify="space-between">
          <Button htmlType="button" disabled={busy || countdown > 0} onClick={() => void send()}>{countdown > 0 ? `${countdown}秒后可重发` : "重新发送真实短信"}</Button>
          <Button type="text" htmlType="button" disabled={busy} onClick={() => { setSent(false); setCountdown(0); setCode(""); setError(""); }}>修改手机号</Button>
        </Flex>}
      </Flex>
    </form>
  </Flex>;
}
